/**
 * NotificationsPage - Pagina dedicata alle notifiche utente
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Bell,
  BellOff,
  CheckCircle,
  Package,
  Users,
  MessageSquare,
  AlertCircle,
  Clock,
  Trash2,
  CheckCheck,
  Loader2,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications/list?limit=100`);
      setNotifications(res.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Errore nel caricamento delle notifiche');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      // Dispatch custom event per aggiornare il badge nel layout
      window.dispatchEvent(new CustomEvent('notification-read'));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Tutte le notifiche sono state segnate come lette');
      // Dispatch custom event per aggiornare il badge nel layout
      window.dispatchEvent(new CustomEvent('notification-read'));
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setMarkingAll(false);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'order_status_changed': Package,
      'new_order': Package,
      'relationship_request': Users,
      'relationship_approved': Users,
      'new_message': MessageSquare,
      'trial_expiring': AlertCircle,
      'system': Bell
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type) => {
    const colors = {
      'order_status_changed': 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
      'new_order': 'text-green-600 bg-green-100 dark:bg-green-900/30',
      'relationship_request': 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
      'relationship_approved': 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
      'new_message': 'text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30',
      'trial_expiring': 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
      'system': 'text-gray-600 bg-gray-100 dark:bg-gray-900/30'
    };
    return colors[type] || 'text-gray-600 bg-gray-100 dark:bg-gray-900/30';
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    if (notif.action_url) {
      navigate(notif.action_url);
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !n.read;
    if (activeTab === 'orders') return n.type?.includes('order');
    if (activeTab === 'system') return n.type === 'system' || n.type === 'trial_expiring';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifiche
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreadCount} non lette
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">
            Resta aggiornato sulle attività del tuo account
          </p>
        </div>
        {unreadCount > 0 && (
          <Button 
            variant="outline" 
            onClick={markAllAsRead}
            disabled={markingAll}
          >
            {markingAll ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCheck className="h-4 w-4 mr-2" />
            )}
            Segna tutte come lette
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            Tutte
            {notifications.length > 0 && (
              <Badge variant="secondary" className="ml-2">{notifications.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="unread">
            Non lette
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">{unreadCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="orders">Ordini</TabsTrigger>
          <TabsTrigger value="system">Sistema</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredNotifications.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <BellOff className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Nessuna notifica</h3>
                <p className="text-muted-foreground">
                  {activeTab === 'unread' 
                    ? 'Hai letto tutte le notifiche!' 
                    : 'Non ci sono notifiche da mostrare'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notif) => {
                const NotifIcon = getNotificationIcon(notif.type);
                const colorClass = getNotificationColor(notif.type);
                
                return (
                  <Card 
                    key={notif.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      !notif.read ? 'border-primary/50 bg-primary/5' : ''
                    }`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                          <NotifIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!notif.read && (
                              <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                            )}
                            <h4 className="font-medium truncate">{notif.title}</h4>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(notif.created_at).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        
                        {notif.action_url && (
                          <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationsPage;
